/**
 * Presentational card for a single line item (invoice or NEX) in reconciliation modals.
 */
import React from 'react';
import { Box, HStack, Text, Tooltip } from '@chakra-ui/react';
import {
    getQtyColumnLabel,
    getQtyColumnTooltip,
    type NexLineQuantityBasis,
} from '../../utils/nexLineQuantity';

export const fmtUsd = (n: number): string =>
    n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export interface LineCardLine {
    title?: string;
    partNumber?: string;
    qty?: number;
    listPrice?: number;
    unitBuyPrice?: number;
    totalBuyPrice?: number;
}

export interface LineCardProps {
    line: LineCardLine;
    /** If true, format prices with $ prefix (used in auto content) */
    prefixDollar?: boolean;
    /** Fields that differ from the comparison line — these get visually highlighted */
    highlightFields?: Set<keyof LineCardLine>;
    invoiceType?: 'received' | 'returned' | 'return_slip' | 'sublet';
    nexLineQuantityBasis?: NexLineQuantityBasis;
}

export function LineCard({
    line,
    prefixDollar = false,
    highlightFields,
    invoiceType,
    nexLineQuantityBasis = 'qtyRec',
}: LineCardProps) {
    const fmt = (x: number | undefined) =>
        x != null ? (prefixDollar ? (x < 0 ? `-$${fmtUsd(Math.abs(x))}` : `$${fmtUsd(x)}`) : fmtUsd(x)) : '—';
    const isHl = (field: keyof LineCardLine) => !!highlightFields?.has(field);
    const qtyLabel = getQtyColumnLabel(invoiceType, nexLineQuantityBasis);
    const qtyTooltip = getQtyColumnTooltip(invoiceType, nexLineQuantityBasis);
    const circleStyle: Record<string, unknown> = {
        position: 'relative' as const,
        display: 'inline-block',
        _after: {
            content: '""',
            position: 'absolute',
            top: '-4px',
            left: '-6px',
            right: '-6px',
            bottom: '-4px',
            border: '2.5px solid',
            borderColor: 'red.500',
            borderRadius: '50% 42% 55% 45% / 42% 58% 44% 52%',
            transform: 'rotate(-2deg)',
            pointerEvents: 'none',
        },
    };
    return (
        <Box bg="white" borderRadius="lg" p={4} border="1px solid" borderColor="gray.200">
            <HStack spacing={4} align="center" flexWrap="wrap">
                <Box flex="1" minW="120px">
                    <Text fontSize="xs" color="gray.500" mb={0.5}>Description</Text>
                    <Text fontSize="sm" fontWeight="medium" color="gray.900" noOfLines={1}>{line.title || '—'}</Text>
                </Box>
                <Box minW="100px">
                    <Text fontSize="xs" color="gray.500" mb={0.5}>Part #</Text>
                    <Text fontSize="sm" fontWeight="medium" color="gray.900" fontFamily="mono" {...(isHl('partNumber') ? circleStyle : {})}>{line.partNumber || '—'}</Text>
                </Box>
                <Box minW="40px" textAlign="center">
                    <Tooltip label={qtyTooltip} hasArrow placement="top">
                        <Box as="span" display="inline-block" cursor="help">
                            <Text
                                fontSize="xs"
                                color="gray.500"
                                mb={0.5}
                                textTransform="uppercase"
                                letterSpacing="0.04em"
                                whiteSpace="nowrap"
                            >
                                {qtyLabel}
                            </Text>
                            <Text fontSize="sm" fontWeight="medium" color="gray.900" {...(isHl('qty') ? circleStyle : {})}>{line.qty ?? '—'}</Text>
                        </Box>
                    </Tooltip>
                </Box>
                <Box minW="80px" textAlign="right">
                    <Text fontSize="xs" color="gray.500" mb={0.5}>List Price</Text>
                    <Text fontSize="sm" fontWeight="medium" color="gray.900" {...(isHl('listPrice') ? circleStyle : {})}>{line.listPrice != null ? fmt(line.listPrice) : '—'}</Text>
                </Box>
                <Box minW="80px" textAlign="right">
                    <Text fontSize="xs" color="gray.500" mb={0.5}>Buy Price</Text>
                    <Text fontSize="sm" fontWeight="medium" color="gray.900" {...(isHl('unitBuyPrice') ? circleStyle : {})}>{line.unitBuyPrice != null ? fmt(line.unitBuyPrice) : '—'}</Text>
                </Box>
                <Box minW="80px" textAlign="right">
                    <Text fontSize="xs" color="gray.500" mb={0.5}>Total</Text>
                    <Text fontSize="sm" fontWeight="bold" color="gray.900" {...(isHl('totalBuyPrice') ? circleStyle : {})}>{line.totalBuyPrice != null ? fmt(line.totalBuyPrice) : '—'}</Text>
                </Box>
            </HStack>
        </Box>
    );
}
